import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { VIDEO_POSTER, VIDEO_URL } from '@/constants/leesritme'

interface VideoDialogProps {
    className?: string
    label: string
    children: ReactNode
}

export const VideoDialog: FC<VideoDialogProps> = ({ className, label, children }): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        if (!mounted) return

        videoRef.current?.play().catch(() => undefined)
    }, [mounted])

    const open = (): void => {
        dialogRef.current?.showModal()

        if (mounted) videoRef.current?.play().catch(() => undefined)
        else setMounted(true)
    }

    const close = (): void => {
        dialogRef.current?.close()
    }

    return (
        <>
            <button type='button' onClick={open} aria-label={label} className={className}>
                {children}
            </button>

            <dialog
                ref={dialogRef}
                aria-label='Leesritme in vier minuten'
                onClose={() => videoRef.current?.pause()}
                onClick={event => {
                    if (event.target === dialogRef.current) close()
                }}
                className='video-dialog m-auto w-[min(68rem,calc(100%_-_1.5rem))] max-w-none overflow-visible bg-transparent p-0 backdrop:bg-ink/85 backdrop:backdrop-blur-sm'
            >
                <div className='relative'>
                    <button
                        type='button'
                        onClick={close}
                        className='absolute -top-14 right-0 inline-flex items-center gap-2 rounded-full bg-paper px-4 py-2 text-sm font-semibold text-ink shadow-soft transition hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun'
                    >
                        <X className='size-4' aria-hidden='true' />
                        Sluiten
                    </button>

                    {mounted && (
                        <video
                            ref={videoRef}
                            src={VIDEO_URL}
                            poster={VIDEO_POSTER}
                            controls
                            playsInline
                            preload='metadata'
                            className='aspect-video w-full rounded-2xl bg-ink shadow-phone sm:rounded-3xl'
                        >
                            Je browser kan deze video niet afspelen. <a href={VIDEO_URL}>Download de video</a>.
                        </video>
                    )}
                </div>
            </dialog>
        </>
    )
}
